import { Outlet } from 'react-router';
import { useAuth } from '~/services/auth';
import { useClubQuery } from '~/services/internalApi/club/hooks/useClubQuery';
import { useLocation } from 'react-router';
import { useIsWaiverGateHeld } from './waiverGateHold';
import { useWaiverStandingQuery } from '~/services/internalApi/waiver';
import { SignWaiversScreen } from './SignWaiversScreen';

/**
 * Blocks the booking and subscription routes until a signed-in member has
 * signed their club's waivers.
 *
 * **Authenticated members only.** A guest booker has no session — signing would
 * 401, and they have no signature history to check — so they pass straight
 * through and are asked at checkout instead, by `WaiverSection`. That split is
 * why the two surfaces exist rather than one.
 *
 * Deliberately does NOT wrap `authRoutes`. `account-setup` and `reset-password`
 * are reachable while authenticated-but-incomplete, and gating them would trap a
 * member who has to finish setup before they can meaningfully sign anything.
 *
 * That still holds, but it no longer means setup ignores waivers: `AccountSetupPage`
 * *captures* consent inline (`useWaiverCapture`) and posts it between `login()` and
 * navigation. Capturing is not gating — the member is asked as part of a form they are
 * already filling in, never walled out of it. This matters for invited and imported
 * members above all: `resign_scope = 'new'` exempts anyone enrolled before a version was
 * published, so for that cohort this gate never fires and setup is the only ask.
 *
 * It is a client-side entry gate, not a server 403 — same reasoning as the
 * mobile gate: enforcing on every request would kill a booking mid-flow, which
 * is why `requireTermsAcceptance` remains unmounted.
 *
 * Failure is open. If standing cannot be fetched the member gets the portal, not
 * a wall.
 *
 * The CHECKOUT gate fails the other way on purpose — `useWaiverSubmission`
 * refuses on an absent answer. The split is deliberate: never lock a paying
 * member out of the whole app over an outage, and never create a booking we
 * cannot prove was covered. Two gates, opposite defaults, both correct.
 *
 * @module features/waivers/WaiverGate
 */
export function WaiverGate() {
  const { isAuthenticated } = useAuth();
  const { data: club } = useClubQuery();
  const clubId = club?.id ?? null;
  // A mounted finish step is completing consent it already captured — the gate
  // must not swap the flow out mid-completion. See waiverGateHold.ts.
  const held = useIsWaiverGateHeld();
  // Receipts render regardless: /booking/confirmed, /subscriptions/confirmed,
  // /demo/confirmed show something that already happened — walling off a
  // just-paid purchase's confirmation behind a waiver is hostile, and the
  // subscription flow deliberately captures no waiver (a waiver binds bay
  // usage, not a purchase). The member meets the gate on their next visit to
  // a gated flow.
  const { pathname } = useLocation();
  // Segment test, not substring — a future '/something-unconfirmed/' path must
  // not silently bypass the gate.
  const isReceipt = pathname.split('/').includes('confirmed');

  const { pending, isLoading, isError } = useWaiverStandingQuery({
    clubId,
    enabled: isAuthenticated,
  });

  // No spinner. The routes below render their own loading states, and flashing a
  // blank gate on every navigation costs more than the rare case of a member
  // seeing the booking page for a moment before the waiver appears.
  if (held || isReceipt || !isAuthenticated || isLoading || isError || clubId == null || pending.length === 0) {
    return <Outlet />;
  }

  return <SignWaiversScreen waivers={pending} clubId={clubId} />;
}

export default WaiverGate;
